import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormItem, TextInput } from '@carbon/react';

const HEX = /^#[0-9a-fA-F]{6}$/;

/** WCAG relative luminance. Mirrors `apps/storefront/src/lib/contrast.ts` — see below for why. */
function luminance(hex: string): number {
  const channels = [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16) / 255);
  const [r, g, b] = channels.map((channel) =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  );
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: string, b: string): number {
  const [la, lb] = [luminance(a), luminance(b)];
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Pure black or pure white, whichever reads better on the accent.
 *
 * This duplicates `readableOn` in the storefront app, and the duplication is deliberate rather
 * than an oversight: the two apps share no code (different React majors, separate lockfiles,
 * `apps/storefront` is not a workspace member), and importing across that boundary is what the
 * isolation exists to prevent. What matters is that both compute the SAME pairing, so the
 * preview here is honest about what the site will render. `AccentField.test.tsx` pins the values
 * against the storefront's own expectations — if the two ever diverge, that test fails.
 */
export function onAccent(accent: string): string {
  return contrast('#000000', accent) >= contrast('#ffffff', accent) ? '#000000' : '#ffffff';
}

interface AccentFieldProps {
  value: string;
  onChange: (next: string) => void;
}

/**
 * The accent colour, with a live preview of the pill it actually produces.
 *
 * The preview is not decoration. The accent is the one theme value the storefront still applies,
 * and it is applied as a BACKGROUND with a derived foreground — so the meaningful question for an
 * owner is not "what colour is this" but "what does my button look like". Showing the derived
 * pairing answers that before they save, and makes it obvious why a very pale colour flips the
 * label to black.
 *
 * A native `<input type="color">` beside the text field, because a hex box alone is a poor way to
 * choose a colour and a picker alone cannot be pasted into from a brand guide.
 */
export const AccentField = React.memo(({ value, onChange }: AccentFieldProps) => {
  const { t } = useTranslation();
  const valid = HEX.test(value ?? '');
  const preview = valid ? value.toLowerCase() : '#c2410c';

  const handle = useCallback((next: string) => onChange(next), [onChange]);

  return (
    <FormItem>
      <div className="storefront-accent">
        <TextInput
          id="accentColor"
          name="accentColor"
          labelText={t('storefront.appearance.accentColor')}
          helperText={t('storefront.appearance.accentHint')}
          value={value ?? ''}
          invalid={Boolean(value) && !valid}
          invalidText={t('storefront.appearance.accentInvalid')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => handle(event.target.value)}
        />
        <input
          type="color"
          aria-label={t('storefront.appearance.accentColor')}
          className="storefront-accent__swatch"
          value={preview}
          onChange={(event) => handle(event.target.value)}
        />
      </div>
      <div className="storefront-accent__preview">
        <span
          className="storefront-accent__pill"
          style={{ backgroundColor: preview, color: onAccent(preview) }}
          data-testid="accent-preview"
        >
          {t('storefront.appearance.accentPreview')}
        </span>
      </div>
    </FormItem>
  );
});

export default AccentField;
